import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useUser, useRefreshUser, useSetUser } from "./UserContext";

const WebSocketContext = createContext({
  websocketUrl: null,
  socketState: "disconnected",
  lastMessage: null,
  onlineByOrganization: {},
});

export const useWebSocket = () => useContext(WebSocketContext);

function normalizeExplicitWsUrl(url) {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return toWebSocketUrl(url);
  }

  return url.replace(/\/$/, "");
}

/** Same host/port as the API; path `/ws` is a dedicated WS endpoint (nginx proxies it to Node). */
function toWebSocketUrl(httpUrl) {
  const parsedUrl = new URL(httpUrl);
  parsedUrl.protocol = parsedUrl.protocol === "https:" ? "wss:" : "ws:";
  parsedUrl.pathname = "/ws";
  parsedUrl.search = "";
  parsedUrl.hash = "";
  return parsedUrl.toString().replace(/\/$/, "");
}

function getWsUrl() {
  if (process.env.REACT_APP_WS_URL) {
    return normalizeExplicitWsUrl(process.env.REACT_APP_WS_URL);
  }

  if (process.env.REACT_APP_API_URL) {
    try {
      return toWebSocketUrl(process.env.REACT_APP_API_URL);
    } catch {
      return process.env.REACT_APP_API_URL;
    }
  }

  const protocol = window.location.protocol === "https:" ? "wss" : "ws";

  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    return `${protocol}://${window.location.hostname}:5000/ws`;
  }

  return `${protocol}://${window.location.host}/ws`;
}

export const WebSocketProvider = ({ children }) => {
  const user = useUser();
  const refreshUser = useRefreshUser();
  const setUser = useSetUser();
  const navigate = useNavigate();
  const reconnectTimeoutRef = useRef(null);
  const [connectionSeed, setConnectionSeed] = useState(0);
  const [websocketUrl, setWebsocketUrl] = useState(null);
  const [socketState, setSocketState] = useState("disconnected");
  const [lastMessage, setLastMessage] = useState(null);
  const [onlineByOrganization, setOnlineByOrganization] = useState({});

  useEffect(() => {
    clearTimeout(reconnectTimeoutRef.current);

    if (!user || user.isPlatformAdmin) {
      setWebsocketUrl(null);
      setSocketState("disconnected");
      setLastMessage(null);
      setOnlineByOrganization({});
      return;
    }

    setSocketState("connecting");

    const baseUrl = getWsUrl();
    setWebsocketUrl(baseUrl);

    const params = new URLSearchParams({
      employeeId: String(user.employeeId || user.id || "unknown"),
      employeeName: user.username || "unknown",
      organizationId: String(user.organizationId || "unknown"),
    });

    let shouldReconnect = true;

    const ws = new WebSocket(`${baseUrl}?${params.toString()}`);

    ws.onopen = () => {
      setSocketState("connected");
    };

    ws.onclose = () => {
      if (!shouldReconnect) {
        return;
      }

      setSocketState("disconnected");
      reconnectTimeoutRef.current = setTimeout(() => {
        setConnectionSeed((value) => value + 1);
      }, 3000);
    };

    ws.onerror = () => {
      setSocketState("error");
    };

    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);

        if (parsed.wsBatch === true && Array.isArray(parsed.messages)) {
          for (const msg of parsed.messages) {
            flushSync(() => {
              setLastMessage(msg);
            });
            if (msg.type === "USER_PRESENCE_SNAPSHOT") {
              const orgKey = String(msg.data?.organizationId ?? "");
              const employeeIds = (msg.data?.onlineUsers || [])
                .map((u) => Number(u.employeeId))
                .filter((id) => !Number.isNaN(id));
              setOnlineByOrganization((prev) => ({
                ...prev,
                [orgKey]: employeeIds,
              }));
            }
          }
          return;
        }

        /** Same as `wsBatch`: flush so list hooks run per frame; otherwise rapid frames batch and only the last `lastMessage` is observed. */
        flushSync(() => {
          setLastMessage(parsed);
        });

        if (parsed.type === "USER_PRESENCE_SNAPSHOT") {
          const orgKey = String(parsed.data?.organizationId ?? "");
          const employeeIds = (parsed.data?.onlineUsers || [])
            .map((u) => Number(u.employeeId))
            .filter((id) => !Number.isNaN(id));

          setOnlineByOrganization((prev) => ({
            ...prev,
            [orgKey]: employeeIds,
          }));
          return;
        }

        if (parsed.type === "USER_ONLINE") {
          const orgKey = String(
            parsed.organizationId ?? parsed.data?.organizationId ?? "",
          );
          const employeeId = Number(
            parsed.data?.employeeId ?? parsed.data?.employee?.id,
          );
          if (!orgKey || Number.isNaN(employeeId)) {
            return;
          }

          setOnlineByOrganization((prev) => {
            const current = prev[orgKey] || [];
            return current.includes(employeeId)
              ? prev
              : { ...prev, [orgKey]: [...current, employeeId] };
          });
          return;
        }

        if (parsed.type === "USER_OFFLINE") {
          const orgKey = String(
            parsed.organizationId ?? parsed.data?.organizationId ?? "",
          );
          const employeeId = Number(parsed.data?.employeeId);
          if (!orgKey || Number.isNaN(employeeId)) {
            return;
          }

          setOnlineByOrganization((prev) => ({
            ...prev,
            [orgKey]: (prev[orgKey] || []).filter((id) => id !== employeeId),
          }));
        }
      } catch {
        // Ignore non-JSON websocket payloads.
      }
    };

    return () => {
      shouldReconnect = false;
      clearTimeout(reconnectTimeoutRef.current);
      ws.close();
    };
  }, [user, connectionSeed]);

  const userId = user?.id;
  const userOrgId = user?.organizationId ?? user?.organization_id;

  useEffect(() => {
    if (!lastMessage || userId == null) return;
    if (lastMessage.type !== "USER_UPDATED") return;
    const orgId =
      lastMessage.organizationId ?? lastMessage.data?.organizationId;
    if (
      orgId != null &&
      userOrgId != null &&
      Number(orgId) !== Number(userOrgId)
    ) {
      return;
    }
    const updatedId = lastMessage.data?.id;
    if (updatedId == null) return;
    if (Number(updatedId) !== Number(userId)) return;
    if (lastMessage.data?.isActive === false) {
      void (async () => {
        try {
          await axios.post("/api/auth/logout");
        } catch {
          // ignore — cookie may already be cleared server-side
        }
        setUser(null);
        navigate("/login", { replace: true });
      })();
      return;
    }
    void refreshUser();
  }, [lastMessage, userId, userOrgId, refreshUser, setUser, navigate]);

  const contextValue = useMemo(
    () => ({ websocketUrl, socketState, lastMessage, onlineByOrganization }),
    [websocketUrl, socketState, lastMessage, onlineByOrganization],
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
